export const RARITY_STYLES = {
  Common: "bg-slate-100 text-slate-700 border-slate-300",
  Scarce: "bg-emerald-100 text-emerald-800 border-emerald-300",
  Rare: "bg-amber-100 text-amber-800 border-amber-300",
  "Very Rare": "bg-indigo-100 text-indigo-800 border-indigo-300",
  "Ultra Rare / Museum": "bg-rose-100 text-rose-800 border-rose-300",
};

export const GRADES = ["M", "A", "B", "C", "D", "R", "X"];

export const GRADE_HELP = {
  M: "Mint — factory-perfect, no wear.",
  A: "Near/Very Near Mint — tiny pings, flea bites, up to 3 blunted drip points; factory features OK.",
  B: "Very Good — wire-groove wear, 4–10 base nibbles or blunted drips, up to 3 broken drips.",
  C: "Average — up to 3 minor chips (<1\" total), a bruise under ½\", 4–10 broken drips.",
  D: "Damaged — chip/slice over 1\", fractures, bruise over ½\", 10+ broken drips.",
  R: "Repaired — value depends on the repair.",
  X: "Altered — irradiated or heated to change color.",
};

export const GRADE_LABEL = {
  M: "M · Mint", A: "A · Near Mint", B: "B · Very Good", C: "C · Average",
  D: "D · Damaged", R: "R · Repaired", X: "X · Altered",
};

export const COLOR_SWATCH = {
  "Honey Amber": "#C9781A",
  Amber: "#C9781A",
  "Root Beer Amber": "#6E3A16",
  Aqua: "#7FD3C7",
  "Ice Aqua": "#CFEDE8",
  "Ice Blue": "#B7DCEA",
  "Green Aqua": "#6FBFA0",
  Teal: "#1F8A8A",
  "Cobalt Blue": "#2545C9",
  "Amethyst Purple": "#8A5AA8",
  "Sun-Colored Amethyst": "#B487C4",
  Purple: "#8A5AA8",
  "Olive Green": "#7A7A2E",
  "7-Up Green": "#3DA35D",
  "Yellow Green": "#9DB03C",
  "Emerald Green": "#1E7A4D",
  "Lime Green": "#8FCB3A",
  Green: "#3DA35D",
  Clear: "#E7E4D8",
  "Milk / Opalescent": "#F0EBE2",
  Carnival: "#A9743E",
  Peach: "#F2B48C",
  Straw: "#E4CC7A",
  "Smoke / Gray": "#9AA0A0",
};

// Neutral fallback (NOT amber) when a color can't be resolved.
const COLOR_FALLBACK = "#B9B4A7";

// Resolve any color name (including free-typed variants) to a hex swatch.
export function colorHex(name) {
  if (!name) return COLOR_FALLBACK;
  if (COLOR_SWATCH[name]) return COLOR_SWATCH[name];
  const key = Object.keys(COLOR_SWATCH).find((k) => k.toLowerCase() === String(name).toLowerCase());
  if (key) return COLOR_SWATCH[key];
  const n = String(name).toLowerCase();
  const has = (...w) => w.every((x) => n.includes(x));
  if (has("ice", "blue")) return COLOR_SWATCH["Ice Blue"];
  if (has("ice")) return COLOR_SWATCH["Ice Aqua"];
  if (n.includes("carnival")) return COLOR_SWATCH["Carnival"];
  if (has("root", "beer")) return COLOR_SWATCH["Root Beer Amber"];
  if (n.includes("amethyst") || n.includes("purple")) return COLOR_SWATCH["Amethyst Purple"];
  if (n.includes("cobalt")) return COLOR_SWATCH["Cobalt Blue"];
  if (n.includes("teal")) return COLOR_SWATCH["Teal"];
  if (n.includes("emerald")) return COLOR_SWATCH["Emerald Green"];
  if (n.includes("lime")) return COLOR_SWATCH["Lime Green"];
  if (n.includes("olive")) return COLOR_SWATCH["Olive Green"];
  if (has("yellow", "green")) return COLOR_SWATCH["Yellow Green"];
  if (n.includes("aqua")) return COLOR_SWATCH["Aqua"];
  if (n.includes("amber") || n.includes("honey")) return COLOR_SWATCH["Honey Amber"];
  if (n.includes("peach")) return COLOR_SWATCH["Peach"];
  if (n.includes("straw") || n.includes("yellow")) return COLOR_SWATCH["Straw"];
  if (n.includes("milk") || n.includes("opal")) return COLOR_SWATCH["Milk / Opalescent"];
  if (n.includes("smoke") || n.includes("gray") || n.includes("grey")) return COLOR_SWATCH["Smoke / Gray"];
  if (n.includes("green")) return COLOR_SWATCH["Green"];
  if (n.includes("blue")) return COLOR_SWATCH["Cobalt Blue"];
  if (n.includes("clear")) return COLOR_SWATCH["Clear"];
  return COLOR_FALLBACK;
}

export const MIN_COLLECTION = 5;

export const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
  "Wisconsin", "Wyoming", "District of Columbia",
];

export const CA_PROVINCES = [
  "Alberta", "British Columbia", "Manitoba", "New Brunswick", "Newfoundland and Labrador",
  "Northwest Territories", "Nova Scotia", "Nunavut", "Ontario", "Prince Edward Island",
  "Quebec", "Saskatchewan", "Yukon Territory",
];

export const AU_STATES = [
  "Australian Capital Territory", "New South Wales", "Northern Territory", "Queensland",
  "South Australia", "Tasmania", "Victoria", "Western Australia",
];

export const DE_STATES = [
  "Baden-Württemberg", "Bayern", "Berlin", "Brandenburg", "Bremen", "Hamburg", "Hessen",
  "Mecklenburg-Vorpommern", "Niedersachsen", "Nordrhein-Westfalen", "Rheinland-Pfalz",
  "Saarland", "Sachsen", "Sachsen-Anhalt", "Schleswig-Holstein", "Thüringen",
];

export const UK_COUNTIES = ["Aberdeen", "Aberdeenshire", "Anglesey", "Angus", "Antrim", "Ards", "Argyll and Bute", "Armagh", "Ballymena", "Ballymoney", "Banbridge", "Barking and Dagenham", "Bath and North East Somerset", "Bedfordshire", "Belfast", "Berkshire", "Bexley", "Blackburn with Darwen", "Blaenau Gwent", "Bournemouth", "Brent", "Bridgend", "Brighton and Hove", "Bristol", "Bromley", "Buckinghamshire", "Caerphilly", "Cambridgeshire", "Camden", "Cardiff", "Carmarthenshire", "Carrickfergus", "Castlereagh", "Ceredigion", "Cheshire", "Clackmannanshire", "Coleraine", "Conwy", "Cookstown", "Cornwall", "Craigavon", "Croydon", "Cumbria", "Darlington", "Denbighshire", "Derby", "Derbyshire", "Derry", "Devon", "Dorset", "Down", "Dumfries and Galloway", "Dundee", "Dungannon", "Durham", "Ealing", "East Ayrshire", "East Dunbartonshire", "East Lothian", "East Renfrewshire", "East Riding of Yorkshire", "East Sussex", "Edinburgh", "Eilean Siar", "Enfield", "Essex", "Falkirk", "Fermanagh", "Fife", "Flintshire", "Glasgow", "Gloucestershire", "Greenwich", "Gwynedd", "Hackney", "Halton", "Hammersmith and Fulham", "Hampshire", "Haringey", "Harrow", "Hartlepool", "Havering", "Herefordshire", "Hertfordshire", "Highland", "Hillingdon", "Hounslow", "Inverclyde", "Isle of Wight", "Islington", "Kensington and Chelsea", "Kent", "Kingston upon Hull", "Kingston upon Thames", "Lambeth", "Lancashire", "Larne", "Leicester", "Leicestershire", "Lewisham", "Limavady", "Lincolnshire", "Lisburn", "London", "Luton", "Magherafelt", "Manchester", "Medway", "Merseyside", "Merthyr Tydfil", "Merton", "Middlesbrough", "Midlothian", "Milton Keynes", "Monmouthshire", "Moray", "Moyle", "Neath Port Talbot", "Newham", "Newport", "Newry and Mourne", "Newtownabbey", "Norfolk", "North Ayrshire", "North Down", "North East Lincolnshire", "North Lanarkshire", "North Lincolnshire", "North Somerset", "North Yorkshire", "Northamptonshire", "Northumberland", "Nottingham", "Nottinghamshire", "Omagh", "Orkney Islands", "Oxfordshire", "Pembrokeshire", "Perthshire and Kinross", "Peterborough", "Plymouth", "Poole", "Portsmouth", "Powys", "Redbridge", "Redcar and Cleveland", "Renfrewshire", "Rhondda, Cynon, Taff", "Richmond upon Thames", "Rutland", "Scottish Borders", "Shetland Islands", "Shropshire", "Somerset", "South Ayrshire", "South Gloucestershire", "South Lanarkshire", "South Yorkshire", "Southampton", "Southend-on-Sea", "Southwark", "Staffordshire", "Stirling", "Stockton-on-Tees", "Stoke-on-Trent", "Strabane", "Suffolk", "Surrey", "Sutton", "Swansea", "Swindon", "Telford and Wrekin", "Thurrock", "Torbay", "Torfaen", "Tower Hamlets", "Tyne and Wear", "Vale of Glamorgan", "Waltham Forest", "Wandsworth", "Warrington", "Warwickshire", "West Dunbartonshire", "West Lothian", "West Midlands", "West Sussex", "West Yorkshire", "Westminster", "Wiltshire", "Worcestershire", "Wrexham", "York"];

// Which countries support a region drill-down, and their region option lists.
export const REGION_OPTIONS = {
  "United States of America": US_STATES,
  Canada: CA_PROVINCES,
  Australia: AU_STATES,
  "United Kingdom": UK_COUNTIES,
  Germany: DE_STATES,
};

// Human label for the region field per country.
export const REGION_LABEL = {
  "United States of America": "US State",
  Canada: "Province",
  Australia: "State / Territory",
  "United Kingdom": "County",
  Germany: "State (Land)",
};

export const COUNTRIES = [
  "United States of America", "Canada", "United Kingdom", "France", "Germany",
  "Australia", "Belgium", "Czechia", "Poland", "Russia", "Japan", "China",
  "India", "Italy", "Netherlands", "Spain", "Sweden", "Mexico", "New Zealand",
  "Argentina", "Brazil", "Austria", "Switzerland",
];

// Country → ISO 3166-1 alpha-2 code (lowercase) for flag seals on passport stamps.
export const COUNTRY_ISO = {
  "United States of America": "us", "United States": "us", USA: "us", US: "us",
  Canada: "ca", "United Kingdom": "gb", UK: "gb", England: "gb", Britain: "gb",
  France: "fr", Germany: "de", Australia: "au", Belgium: "be", Czechia: "cz",
  "Czech Republic": "cz", Poland: "pl", Russia: "ru", Japan: "jp", China: "cn",
  India: "in", Italy: "it", Netherlands: "nl", Spain: "es", Sweden: "se",
  Mexico: "mx", "New Zealand": "nz", Argentina: "ar", Brazil: "br", Austria: "at",
  Switzerland: "ch", Ireland: "ie", Norway: "no", Denmark: "dk", Finland: "fi",
  Portugal: "pt", Greece: "gr", "South Africa": "za", "South Korea": "kr",
};
export const flagUrl = (country) => {
  const iso = COUNTRY_ISO[country];
  return iso ? `https://flagcdn.com/w160/${iso}.png` : null;
};

// Faded ink-stamp national animals (AI-generated passport-stamp emblems).
const ANIMAL = {
  eagle: "https://static.prod-images.emergentagent.com/jobs/280a934b-a6d8-4a11-9aef-a66fb0aeb566/images/e8ac0f500d9355e5aa5e17f8d0f3aea0cb5cd982017f4b0dbbe049556347a4c4.jpeg",
  lion: "https://static.prod-images.emergentagent.com/jobs/280a934b-a6d8-4a11-9aef-a66fb0aeb566/images/514a80f4bfc38616dfb55b0abbfbe6d9488b7b1f615d69b6bde5fbd39064ca6e.jpeg",
  moose: "https://static.prod-images.emergentagent.com/jobs/280a934b-a6d8-4a11-9aef-a66fb0aeb566/images/4d4e172918199f0c93dc4ffa63d274ddb9473e7da35c284a6e4ea4f75dcb2639.jpeg",
  kangaroo: "https://static.prod-images.emergentagent.com/jobs/280a934b-a6d8-4a11-9aef-a66fb0aeb566/images/335e4ad96f1f509eb11fb2ec0d6519b3602baf8d523632e7977681a8375694d7.jpeg",
  rooster: "https://static.prod-images.emergentagent.com/jobs/280a934b-a6d8-4a11-9aef-a66fb0aeb566/images/936dfb126dd3fc5582f12dc989ed612f3d4be5c01a7a7d41e2e6ada0c82ef6bf.jpeg",
  bear: "https://static.prod-images.emergentagent.com/jobs/280a934b-a6d8-4a11-9aef-a66fb0aeb566/images/301846dae6c78dbc0bbe58db6016aa9e9a7917da0689b8b3b5f091539418cdcf.jpeg",
  crane: "https://static.prod-images.emergentagent.com/jobs/280a934b-a6d8-4a11-9aef-a66fb0aeb566/images/1bd68c7ac255c513339ceb6c80e547b8d0785aa74df3735fa1ab99362a15168c.jpeg",
  panda: "https://static.prod-images.emergentagent.com/jobs/280a934b-a6d8-4a11-9aef-a66fb0aeb566/images/8f8f5fd999136524e8ac82a6bfedb7cddb997ed36aacf691127db2f8aae39425.jpeg",
  tiger: "https://static.prod-images.emergentagent.com/jobs/280a934b-a6d8-4a11-9aef-a66fb0aeb566/images/a0f38385fc693818cafb478961bc825cb45ec1f20d20f711ec88c0a7aa9e25e1.jpeg",
  jaguar: "https://static.prod-images.emergentagent.com/jobs/280a934b-a6d8-4a11-9aef-a66fb0aeb566/images/1ece9891637e5ed298f02fe563e6cf97059725005e50a2a301ebd0ff5ad58170.jpeg",
  wolf: "https://static.prod-images.emergentagent.com/jobs/280a934b-a6d8-4a11-9aef-a66fb0aeb566/images/e9ed737f3c8217c3cf2d2c3cd45a57259393d6c089a0dfeec2015ea083381a8b.jpeg",
  bull: "https://static.prod-images.emergentagent.com/jobs/280a934b-a6d8-4a11-9aef-a66fb0aeb566/images/7d0251f1130a93cd0920c0929c5da0fce350a9d3e3b2c387db07e0edc2fe726d.jpeg",
  kiwi: "https://static.prod-images.emergentagent.com/jobs/280a934b-a6d8-4a11-9aef-a66fb0aeb566/images/7a411ea8c65a04b85fbf38835b8c73dd2a1dd8a163eac476b4438b3e018ee8e9.jpeg",
  ibex: "https://static.prod-images.emergentagent.com/jobs/280a934b-a6d8-4a11-9aef-a66fb0aeb566/images/46818a481ca6e7dfd5d146bb8f005766548ab11ebbc2fb09321d42562a17cab7.jpeg",
  puma: "https://static.prod-images.emergentagent.com/jobs/280a934b-a6d8-4a11-9aef-a66fb0aeb566/images/af31ab95a5d00265049a03e5c2b372b8852d2bf82196194eefd11ec48c401b15.jpeg",
};
const COUNTRY_ANIMAL_KEY = {
  "United States of America": "eagle", "United States": "eagle", USA: "eagle", US: "eagle",
  Germany: "eagle", Mexico: "eagle", Poland: "eagle", Austria: "eagle",
  "United Kingdom": "lion", Netherlands: "lion", Belgium: "lion", Czechia: "lion", "Czech Republic": "lion",
  Canada: "moose", Sweden: "moose",
  Australia: "kangaroo", France: "rooster", Russia: "bear", Japan: "crane", China: "panda",
  India: "tiger", Brazil: "jaguar", Italy: "wolf", Spain: "bull", "New Zealand": "kiwi",
  Switzerland: "ibex", Argentina: "puma",
};
export const animalUrl = (country) => ANIMAL[COUNTRY_ANIMAL_KEY[country]] || null;

// Primary national-flag colour used to "ink" each country's animal stamp.
const COUNTRY_INK = {
  "United States of America": "#3C3B6E", "United States": "#3C3B6E", USA: "#3C3B6E", US: "#3C3B6E",
  Canada: "#D52B1E", Australia: "#00843D", Russia: "#D52B1E", "United Kingdom": "#012169",
  Germany: "#C1121C", France: "#0055A4", Japan: "#BC002D", China: "#DE2910", India: "#FF9933",
  Mexico: "#006847", Brazil: "#009C3B", Italy: "#008C45", Spain: "#C60B1E", Sweden: "#006AA7",
  "New Zealand": "#00247D", Switzerland: "#D52B1E", Argentina: "#3C8DBC", Netherlands: "#AE1C28",
  Belgium: "#ED2939", Czechia: "#11457E", "Czech Republic": "#11457E", Poland: "#DC143C", Austria: "#ED2939",
};
export const inkColor = (country) => COUNTRY_INK[country] || "#78350f";
